import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  makeStyles,
  Paper,
  Theme,
  Typography,
} from '@material-ui/core';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  getProjectTestCaseTemplate,
  ProjectTestCaseTemplate,
  ProjectsApiOptions,
  uploadProjectTestCaseTemplate,
  revertProjectTestCaseTemplate,
} from '../../api/projects';

type TestCaseTemplateSectionProps = {
  projectId: string;
  canManage: boolean;
  forcePreviewMode?: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
  section: {
    marginTop: theme.spacing(4),
  },
  card: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    borderRadius: theme.spacing(1),
  },
  templateCard: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    borderRadius: theme.spacing(1),
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  templateIcon: {
    color: theme.palette.primary.main,
  },
  subtle: {
    color: theme.palette.text.secondary,
  },
  success: {
    color: theme.palette.success.main,
  },
  error: {
    color: theme.palette.error.main,
  },
  fileInput: {
    display: 'none',
  },
  selectedFileBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginTop: theme.spacing(3),
  },
}));

const previewStorageKey = (projectId: string) =>
  `ses-project-test-case-template-${projectId}`;

const defaultTemplate: ProjectTestCaseTemplate = {
  source: 'PROJECT_TEST_CASE_TEMPLATE_SOURCE_PLATFORM_DEFAULT',
};

function loadPreviewTemplate(projectId: string): ProjectTestCaseTemplate {
  if (typeof window === 'undefined') {
    return defaultTemplate;
  }

  const stored = window.sessionStorage.getItem(previewStorageKey(projectId));
  if (!stored) {
    return defaultTemplate;
  }

  try {
    return JSON.parse(stored) as ProjectTestCaseTemplate;
  } catch {
    return defaultTemplate;
  }
}

function savePreviewTemplate(
  projectId: string,
  template: ProjectTestCaseTemplate,
) {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(
    previewStorageKey(projectId),
    JSON.stringify(template),
  );
}

export const TestCaseTemplateSection = ({
  projectId,
  canManage,
  forcePreviewMode,
}: TestCaseTemplateSectionProps) => {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const config = useApi(configApiRef);

  const isProjectPreviewMode =
    forcePreviewMode ??
    config.getOptionalBoolean('ses.projectPreviewMode') ??
    false;

  const backendUrl = config.getOptionalString('backend.baseUrl') ?? '';
  const projectsProxyPath =
    config.getOptionalString('ses.projectsApi.proxyPath') ??
    '/api/proxy/ses-projects';

  const apiOptions = useMemo<ProjectsApiOptions>(
    () => ({
      baseUrl: `${backendUrl}${projectsProxyPath}`,
    }),
    [backendUrl, projectsProxyPath],
  );

  const [template, setTemplate] =
    useState<ProjectTestCaseTemplate>(defaultTemplate);
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | undefined>();
  const [successKey, setSuccessKey] = useState<string | undefined>();

  const loadTemplate = useCallback(async () => {
    setLoading(true);
    setErrorKey(undefined);
    setSuccessKey(undefined);

    try {
      if (isProjectPreviewMode) {
        setTemplate(loadPreviewTemplate(projectId));
        return;
      }

      const response = await getProjectTestCaseTemplate(apiOptions, projectId);
      setTemplate(response?.template ?? defaultTemplate);
    } catch {
      setErrorKey('testCaseTemplate.loadError');
    } finally {
      setLoading(false);
    }
  }, [apiOptions, isProjectPreviewMode, projectId]);

  useEffect(() => {
    void loadTemplate();
  }, [loadTemplate]);

  const handleUpload = async () => {
    if (!selectedFile) {
      return;
    }

    setSaving(true);
    setErrorKey(undefined);
    setSuccessKey(undefined);

    try {
      if (isProjectPreviewMode) {
        const nextTemplate: ProjectTestCaseTemplate = {
          source: 'PROJECT_TEST_CASE_TEMPLATE_SOURCE_CUSTOM',
          fileName: selectedFile.name,
          uploadedAt: new Date().toISOString(),
          version: (template.version ?? 0) + 1,
        };

        savePreviewTemplate(projectId, nextTemplate);
        setTemplate(nextTemplate);
      } else {
        await uploadProjectTestCaseTemplate(
          apiOptions,
          projectId,
          selectedFile,
        );

        const response = await getProjectTestCaseTemplate(
          apiOptions,
          projectId,
        );

        setTemplate(response.template ?? defaultTemplate);
      }

      setSelectedFile(undefined);
      setSuccessKey('testCaseTemplate.uploadSuccess');
    } catch {
      setErrorKey('testCaseTemplate.uploadError');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async () => {
    setSaving(true);
    setErrorKey(undefined);
    setSuccessKey(undefined);

    try {
      if (isProjectPreviewMode) {
        savePreviewTemplate(projectId, defaultTemplate);
        setTemplate(defaultTemplate);
      } else {
        if (!template.templateId) {
          setErrorKey('testCaseTemplate.revertError');
          return;
        }

        const response = await revertProjectTestCaseTemplate(
          apiOptions,
          projectId,
          template.templateId,
        );

        setTemplate(response.template ?? defaultTemplate);
      }

      setSelectedFile(undefined);
      setSuccessKey('testCaseTemplate.revertSuccess');
    } catch {
      setErrorKey('testCaseTemplate.revertError');
    } finally {
      setSaving(false);
    }
  };

  const isCustomTemplate =
    template.source === 'PROJECT_TEST_CASE_TEMPLATE_SOURCE_CUSTOM';

  return (
    <Box className={classes.section}>
      <Typography variant="h5">{t('testCaseTemplate.title')}</Typography>

      <Paper variant="outlined" className={classes.card}>
        <Typography variant="subtitle1">
          {t('testCaseTemplate.selectedTemplate')}
        </Typography>

        {loading ? (
          <Box mt={2}>
            <Typography variant="body2" className={classes.subtle}>
              {t('testCaseTemplate.loading')}
            </Typography>
          </Box>
        ) : (
          <Paper variant="outlined" className={classes.templateCard}>
            <DescriptionOutlinedIcon className={classes.templateIcon} />

            <Box flex={1}>
              <Box display="flex" alignItems="center" gridGap={8}>
                <Typography variant="subtitle2">
                  {isCustomTemplate
                    ? template.fileName
                    : t('testCaseTemplate.platformDefault')}
                </Typography>

                <Chip
                  size="small"
                  color={isCustomTemplate ? 'primary' : 'default'}
                  label={
                    isCustomTemplate
                      ? t('testCaseTemplate.custom')
                      : t('testCaseTemplate.default')
                  }
                />
              </Box>
            </Box>

            {isCustomTemplate && (
              <Button
                variant="outlined"
                disabled={!canManage || saving}
                onClick={handleRevert}
              >
                {t('testCaseTemplate.revert')}
              </Button>
            )}
          </Paper>
        )}
      </Paper>

      <Paper variant="outlined" className={classes.card}>
        <Typography variant="subtitle1">
          {t('testCaseTemplate.uploadCustomTemplate')}
        </Typography>

        <Box className={classes.selectedFileBox}>
          <Typography variant="body2">
            {selectedFile?.name ?? t('testCaseTemplate.noFileSelected')}
          </Typography>

          <Typography variant="body2" className={classes.subtle}>
            {t('testCaseTemplate.supportedFormats')}
          </Typography>
        </Box>

        <div className={classes.actions}>
          <input
            id={`test-case-template-upload-${projectId}`}
            className={classes.fileInput}
            type="file"
            accept=".csv,.txt,.pdf,.docx"
            disabled={!canManage || saving}
            onChange={event => {
              setSelectedFile(event.target.files?.[0]);
              setSuccessKey(undefined);
              setErrorKey(undefined);
            }}
          />

          <label htmlFor={`test-case-template-upload-${projectId}`}>
            <Button
              component="span"
              variant="outlined"
              disabled={!canManage || saving}
            >
              {t('testCaseTemplate.browse')}
            </Button>
          </label>

          <Button
            color="primary"
            variant="contained"
            disabled={!canManage || !selectedFile || saving}
            onClick={handleUpload}
          >
            {isCustomTemplate
              ? t('testCaseTemplate.replace')
              : t('testCaseTemplate.upload')}
          </Button>
        </div>
      </Paper>

      {!canManage && (
        <Box mt={2}>
          <Typography variant="body2" className={classes.subtle}>
            {t('testCaseTemplate.permissionHint')}
          </Typography>
        </Box>
      )}

      {successKey && (
        <Box mt={2}>
          <Typography variant="body2" className={classes.success}>
            {t(successKey)}
          </Typography>
        </Box>
      )}

      {errorKey && (
        <Box mt={2}>
          <Typography variant="body2" className={classes.error}>
            {t(errorKey)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
